import { Dispatch, SetStateAction, use, useEffect, useState } from 'react';
import { ControllerRenderProps, FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import OrganisationTreeNode, { findUpstreamParentBranch } from 'shared/features/organisation/models/organisation-tree-node';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';
import { DeviceType, isG2Device } from 'shared/models/device-type';
import DeviceDetailsProviderContext from 'shared/providers/device-details/contexts/device-details-provider-context';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';
import Device, { isPairedWirelessIpUnitDevice } from 'features/device/domain/models/device';
import { useValidateUniqueDesoResidentTransmitter } from 'features/device/domain/queries/deso-queries';

type DeviceSettingsOrganisation = {
  field: ControllerRenderProps<FieldValues, 'organisationUnitId'>;
  currentViewingMode: ViewingMode;
  upstreamParentBranch: OrganisationTreeNode[];
  currentGatewayId?: string;
  currentSystemId?: string;
  currentDevice?: Device;

  isOrganisationPickerOpen: boolean;
  isLinkErrorPopupOpen: boolean;
  unlinkConfirmationWarningOpen: boolean;
  unlinkWarningConfirmed: boolean;

  setIsLinkErrorPopupOpen: Dispatch<SetStateAction<boolean>>;
  setUnlinkConfirmationWarningOpen: Dispatch<SetStateAction<boolean>>;
  setOrganisationUnit: (
    field: ControllerRenderProps<FieldValues, 'organisationUnitId'>,
    selectedNode?: OrganisationTreeNode | null,
    upstreamBranch?: OrganisationTreeNode[] | null,
  ) => void;
  setIsOrganisationPickerOpen: Dispatch<SetStateAction<boolean>>;
  handleOnUnlinkOrganisationUnit: (field: ControllerRenderProps<FieldValues, 'organisationUnitId'>) => void;
  handleWarningConfirmed: () => void;
};

const useDeviceSettingsOrganisation = (): DeviceSettingsOrganisation => {
  const { organisationUnitTree } = useOrganisationUnitTreeContext();
  const { currentViewingMode, currentDevice, currentGatewayId, currentSystemId } = useDeviceDetailsHook();
  const { form, setLinkedOrganisationErrorMessage, unlinkWarningConfirmed, updateUnlinkWarningConfirmed } =
    use(DeviceDetailsProviderContext);

  const { mutate: validateUniqueDesoResidentTransmitterMutation, error: validateUniqueDesoResidentTransmitterError } =
    useValidateUniqueDesoResidentTransmitter();

  const [isOrganisationPickerOpen, setIsOrganisationPickerOpen] = useState(false);
  const [upstreamParentBranch, setUpstreamParentBranch] = useState<OrganisationTreeNode[]>([]);
  const [isLinkErrorPopupOpen, setIsLinkErrorPopupOpen] = useState<boolean>(false);
  const [unlinkConfirmationWarningOpen, setUnlinkConfirmationWarningOpen] = useState<boolean>(false);

  const { t } = useTranslation('deviceDetails', { keyPrefix: 'info' });

  const { field } = useController({ name: 'organisationUnitId' });
  const deviceTypeWatch = form.watch('type');

  const setOrganisationUnit = (
    field: ControllerRenderProps<FieldValues, 'organisationUnitId'>,
    selectedNode: OrganisationTreeNode | null = null,
    upstreamBranch: OrganisationTreeNode[] | null = null,
  ): void => {
    field.onChange(selectedNode?.id ?? null);
    setUpstreamParentBranch(upstreamBranch ?? []);
    setIsOrganisationPickerOpen(false);
  };

  const handleOnUnlinkOrganisationUnit = (field: ControllerRenderProps<FieldValues, 'organisationUnitId'>): void => {
    if (
      (currentDevice?.type === DeviceType.DesoDetectionLoop || currentDevice?.type === DeviceType.DomipOutput) &&
      currentDevice?.hasWanderingBoundaryPassages
    ) {
      setIsLinkErrorPopupOpen(true);
    } else if (currentDevice?.type && isG2Device(currentDevice.type) && isPairedWirelessIpUnitDevice(currentDevice.system?.type)) {
      setUnlinkConfirmationWarningOpen(true);
    } else {
      setOrganisationUnit(field, null, null);
      setLinkedOrganisationErrorMessage(undefined);
    }
  };

  const handleWarningConfirmed = (): void => {
    setOrganisationUnit(field, null, null);
    setLinkedOrganisationErrorMessage(undefined);
    setUnlinkConfirmationWarningOpen(false);
    updateUnlinkWarningConfirmed(true);
  };

  useEffect(() => {
    if (organisationUnitTree && currentDevice?.organisationUnitId) {
      const upstreamBranch = findUpstreamParentBranch(organisationUnitTree, currentDevice.organisationUnitId, []);
      setUpstreamParentBranch(upstreamBranch ?? []);
    }
  }, [organisationUnitTree, currentDevice, currentViewingMode]);

  useEffect(() => {
    if (organisationUnitTree && field.value && findUpstreamParentBranch(organisationUnitTree, field.value, []) == null) {
      if (!organisationUnitTree.some((org) => org.id === field.value)) {
        setOrganisationUnit(field, null, null);
        return;
      }
    }
  }, [organisationUnitTree, field]);

  useEffect(() => {
    if (
      upstreamParentBranch.length > 0 &&
      (currentDevice?.type === DeviceType.DesoResidentTransmitter || form.getValues('type') === DeviceType.DesoResidentTransmitter)
    ) {
      validateUniqueDesoResidentTransmitterMutation({ organisationUnitId: upstreamParentBranch[0].id });
    }
  }, [upstreamParentBranch, currentDevice, t]);

  useEffect(() => {
    if (validateUniqueDesoResidentTransmitterError) {
      setLinkedOrganisationErrorMessage(t('deviceAlreadyLinkedToOrganisationUnit'));
    } else {
      setLinkedOrganisationErrorMessage(undefined);
    }
  }, [validateUniqueDesoResidentTransmitterError, setLinkedOrganisationErrorMessage, t]);

  useEffect(() => {
    setLinkedOrganisationErrorMessage(undefined);
  }, [deviceTypeWatch]);

  return {
    field,
    currentViewingMode,
    upstreamParentBranch,
    currentGatewayId,
    currentSystemId,
    currentDevice,

    isOrganisationPickerOpen,
    isLinkErrorPopupOpen,
    unlinkConfirmationWarningOpen,
    unlinkWarningConfirmed,

    setIsLinkErrorPopupOpen,
    setUnlinkConfirmationWarningOpen,
    setOrganisationUnit,
    setIsOrganisationPickerOpen,
    handleOnUnlinkOrganisationUnit,
    handleWarningConfirmed,
  };
};

export default useDeviceSettingsOrganisation;
